import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TableColumn } from "@/types/component";
import { reportSettingsApi } from "../services/report-settings.api";

export interface ReportColumnDescriptor {
  key: string;
  label: string;
  /** Kullanıcı gizleyemez (ör. satırı tanımlayan ilk kolon). */
  alwaysVisible: boolean;
  visible: boolean;
}

export interface UseReportTableSettingsResult<T> {
  /** Kullanıcı tercihine göre sıralanmış ve filtrelenmiş kolonlar. */
  columns: TableColumn<T>[];
  /** Ayar panelinde gösterilecek kolon listesi (gizli olanlar dahil). */
  descriptors: ReportColumnDescriptor[];
  isLoading: boolean;
  isSaving: boolean;
  toggleColumn: (key: string) => void;
  moveColumn: (key: string, direction: "up" | "down") => void;
  reset: () => void;
  /** Varsayılandan farklı bir düzen uygulanmış mı (rozet göstermek için). */
  isCustomized: boolean;
}

/**
 * Rapor tablolarında kolon seçme/gizleme ve sıralama (R11).
 *
 * Rapor sayfalarının kolonları koda gömülüydü; kullanıcı ne kolon gizleyebiliyor
 * ne de sırasını değiştirebiliyordu. Bu hook tercihi sunucuda kullanıcı bazında
 * saklar, yüklenene kadar raporun varsayılan kolonlarını gösterir ve her
 * değişikliği anında kalıcı hale getirir.
 *
 * Kolonların `key` alanı olmalıdır; başlıklar çeviriye göre değiştiği için
 * kalıcı kimlik olarak kullanılamaz. `key` taşımayan kolonlar yapılandırma
 * dışında bırakılır ve her zaman görünür kalır.
 */
export function useReportTableSettings<T>(
  reportKey: string,
  baseColumns: TableColumn<T>[],
): UseReportTableSettingsResult<T> {
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const loadedRef = useRef(false);

  const configurable = useMemo(() => baseColumns.filter((c) => !!c.key), [baseColumns]);
  const defaultOrder = useMemo(() => configurable.map((c) => c.key as string), [configurable]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    reportSettingsApi
      .getTablePreference(reportKey)
      .then((pref) => {
        if (cancelled) return;
        setColumnOrder(pref.columnOrder ?? []);
        setHiddenColumns(pref.hiddenColumns ?? []);
        loadedRef.current = true;
      })
      .catch(() => {
        // Tercih okunamazsa rapor varsayılan düzeniyle çalışmaya devam etmeli.
        if (!cancelled) loadedRef.current = true;
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reportKey]);

  const persist = useCallback(
    (nextOrder: string[], nextHidden: string[]) => {
      setIsSaving(true);
      reportSettingsApi
        .saveTablePreference(reportKey, { columnOrder: nextOrder, hiddenColumns: nextHidden })
        .catch(() => {
          /* hata toast'ı interceptor tarafından gösterilir; yerel durum korunur */
        })
        .finally(() => setIsSaving(false));
    },
    [reportKey],
  );

  /**
   * Etkin sıra: kaydedilmiş sıra + kaydedildikten sonra eklenen yeni kolonlar.
   * Yeni bir kolon sürüm çıktığında kullanıcının eski tercihi onu yutmamalı.
   */
  const effectiveOrder = useMemo(() => {
    const known = columnOrder.filter((key) => defaultOrder.includes(key));
    const missing = defaultOrder.filter((key) => !known.includes(key));
    return [...known, ...missing];
  }, [columnOrder, defaultOrder]);

  const columns = useMemo(() => {
    const byKey = new Map(configurable.map((c) => [c.key as string, c]));
    const ordered = effectiveOrder
      .map((key) => byKey.get(key))
      .filter((c): c is TableColumn<T> => !!c)
      .filter((c) => c.alwaysVisible || !hiddenColumns.includes(c.key as string));

    // `key` taşımayan kolonlar yapılandırılamaz; orijinal yerlerini koruyamadığımız
    // için sona eklenir. Rapor tablolarında tüm kolonlara key verilmesi beklenir.
    const unconfigurable = baseColumns.filter((c) => !c.key);
    return [...ordered, ...unconfigurable];
  }, [configurable, baseColumns, effectiveOrder, hiddenColumns]);

  const descriptors = useMemo<ReportColumnDescriptor[]>(() => {
    const byKey = new Map(configurable.map((c) => [c.key as string, c]));
    return effectiveOrder
      .map((key) => byKey.get(key))
      .filter((c): c is TableColumn<T> => !!c)
      .map((c) => ({
        key: c.key as string,
        label: c.label ?? (typeof c.header === "string" ? c.header : (c.key as string)),
        alwaysVisible: !!c.alwaysVisible,
        visible: !!c.alwaysVisible || !hiddenColumns.includes(c.key as string),
      }));
  }, [configurable, effectiveOrder, hiddenColumns]);

  const toggleColumn = useCallback(
    (key: string) => {
      const target = configurable.find((c) => c.key === key);
      if (target?.alwaysVisible) return;

      const next = hiddenColumns.includes(key)
        ? hiddenColumns.filter((k) => k !== key)
        : [...hiddenColumns, key];

      // Son görünür kolonun da gizlenmesi tabloyu kullanılamaz hale getirir.
      const visibleCount = effectiveOrder.filter((k) => !next.includes(k)).length;
      if (visibleCount === 0) return;

      setHiddenColumns(next);
      persist(effectiveOrder, next);
    },
    [configurable, hiddenColumns, effectiveOrder, persist],
  );

  const moveColumn = useCallback(
    (key: string, direction: "up" | "down") => {
      const index = effectiveOrder.indexOf(key);
      if (index === -1) return;
      const target = direction === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= effectiveOrder.length) return;

      const next = [...effectiveOrder];
      [next[index], next[target]] = [next[target], next[index]];
      setColumnOrder(next);
      persist(next, hiddenColumns);
    },
    [effectiveOrder, hiddenColumns, persist],
  );

  const reset = useCallback(() => {
    setColumnOrder([]);
    setHiddenColumns([]);
    setIsSaving(true);
    reportSettingsApi
      .resetTablePreference(reportKey)
      .catch(() => undefined)
      .finally(() => setIsSaving(false));
  }, [reportKey]);

  const isCustomized =
    hiddenColumns.length > 0 || (columnOrder.length > 0 && columnOrder.join("|") !== defaultOrder.join("|"));

  return { columns, descriptors, isLoading, isSaving, toggleColumn, moveColumn, reset, isCustomized };
}
